import { Vue, Component, Watch } from "vue-property-decorator";
import { cloneDeep, sortBy } from "lodash";
import { ReportsModule } from "@/modules/reports-objective";
import { read, utils } from "xlsx";
import { ActionItem, fmtMsg, MessageHelper, NotificationType } from "commonui";
import { Button, Col, Drawer, Icon, Row, Select, Switch, Table, Upload } from "ant-design-vue";
import { VersionGroupModule } from "@/modules/versiongroup";
import {
	ComponentObjectiveReportService,
	IComponentObjectiveReportService,
	UnitRangeQueryParamsModel
} from "@/services/component-objective-report";
import { UnitRangeModel } from "@/services/objective-manager";
import { ReportsLocale } from "@/locales/localeid";
import LabelText from "@/components/label-text/label-text.vue";
import UnitSelect from "@/components/unit-select/unit-select.vue";
import { MediaSlot } from "@/util/enum";
import {
	IMediaChangeImpactReportService,
	MediaChangeImpactReportService,
	MediaReportComponent
} from "@/services/media-change-impact-report";
import { MediaSlotTypeMapForView } from "@/util/constants";

export interface TableData {
	unit: string;
	component: string;
	mediaType: string;
}

@Component({
	components: {
		Option: Select.Option,
		Select,
		UnitSelect,
		Upload,
		LabelText,
		Drawer,
		Button,
		Table,
		ActionItem,
		Icon,
		Row,
		Col,
		SwitchEl: Switch
	}
})
export default class MediaChangeImpactHeaderActions extends Vue {
	public selectedContentId: string = "";
	public isComponentDropdownLoading: boolean = false;
	public selectedMediaSlotId: number = MediaSlot.ViewingContent;
	public isDrawerVisible: boolean = false;
	public components = [];
	public filteredComponents: MediaReportComponent[] = [];
	public dataFromCSV: any = [];
	public unitObject: UnitRangeModel = {} as UnitRangeModel;
	public convertedData: any = [];
	public convertedDataForTable: any = [];
	public uploadedFileName: string = "";
	public acceptedExtension: string = ".csv";
	public considerUploadedData: boolean = false;
	public versionGroupModule = VersionGroupModule;
	public errors: number[] = [];

	componentObjectiveReportService: IComponentObjectiveReportService = ComponentObjectiveReportService();
	mediaChangeImpactReportService: IMediaChangeImpactReportService = MediaChangeImpactReportService();

	mediaSlots = [
		{ title: MediaSlotTypeMapForView[MediaSlot.ViewingContent], id: MediaSlot.ViewingContent },
		{ title: MediaSlotTypeMapForView[MediaSlot.AlternateContent], id: MediaSlot.AlternateContent },
		{ title: MediaSlotTypeMapForView[MediaSlot.Audio], id: MediaSlot.Audio },
		{ title: MediaSlotTypeMapForView[MediaSlot.AudioBackground], id: MediaSlot.AudioBackground },
		{ title: MediaSlotTypeMapForView[MediaSlot.StreamingVideo], id: MediaSlot.StreamingVideo },
		{ title: MediaSlotTypeMapForView[MediaSlot.OfflineVideo], id: MediaSlot.OfflineVideo },
		{ title: MediaSlotTypeMapForView[MediaSlot.TeacherManual], id: MediaSlot.TeacherManual },
		{ title: MediaSlotTypeMapForView[MediaSlot.Thumbnail], id: MediaSlot.Thumbnail },
		{ title: MediaSlotTypeMapForView[MediaSlot.ExposureAudio], id: MediaSlot.ExposureAudio },
		{ title: MediaSlotTypeMapForView[MediaSlot.ExposureVideo], id: MediaSlot.ExposureVideo },
		{ title: MediaSlotTypeMapForView[MediaSlot.Image], id: MediaSlot.Image },
		{ title: MediaSlotTypeMapForView[MediaSlot.IndividualAudio], id: MediaSlot.IndividualAudio }
	];

	public tableColumns = [
		{
			title: "S.No.",
			dataIndex: "Index",
			key: "index"
		},
		{
			title: "Unit",
			dataIndex: "Unit",
			key: "unit"
		},
		{
			title: "Component",
			dataIndex: "Component",
			key: "component"
		},
		{
			title: "Media Type",
			dataIndex: "MediaTypeId",
			key: "mediaType"
		}
	];

	getData() {
		this.considerUploadedData ? this.getReportDataFromUpload() : this.getReportDataFromFilters();
	}

	get vcsLevelUnits() {
		return sortBy(ReportsModule.vcsUnits, "unit");
	}
	setSelectedUnit(value) {
		ReportsModule.setSelectedUnit(value);
		this.setUnitObject(value);
	}
	get componentText(): string {
		return fmtMsg(ReportsLocale.ReportsComponentText);
	}

	handleContentChange(value) {
		this.selectedContentId = value;
	}

	handleMediaSlotChange(value) {
		this.selectedMediaSlotId = value;
	}

	setUnitObject(value) {
		this.unitObject = { startUnitId: value.id, endUnitId: value.id };
	}

	@Watch("unitObject")
	async getUnitsByRange() {
		this.isComponentDropdownLoading = true;
		const queryParams: UnitRangeQueryParamsModel = {
			gsVersionId: VersionGroupModule.selectedGsVersion,
			lsVersionId: VersionGroupModule.selectedLsVersion
		};
		this.componentObjectiveReportService
			.getComponentsByUnitRange(this.unitObject.startUnitId, this.unitObject.endUnitId, queryParams)
			.then((res: any) => {
				this.setComponents(res);
				this.filteredComponents = res;
				this.selectedContentId = res[0].contentId;
				this.isComponentDropdownLoading = false;
			})
			.catch((err) => {
				console.log(err);
				this.isComponentDropdownLoading = false;
			});
	}

	@Watch("versionGroupModule.selectedGsVersion", { immediate: true })
	isGSVersionUpdated() {
		if (this.versionGroupModule.selectedGsVersion !== "-1") {
			this.versionGroupModule.setLsVersion("-1");
		}
	}

	@Watch("versionGroupModule.selectedLsVersion", { immediate: true })
	isLSVersionUpdated() {
		if (this.versionGroupModule.selectedLsVersion !== "-1") {
			this.versionGroupModule.setGsVersion("-1");
		}
	}

	onToggle(value) {
		this.selectedMediaSlotId = MediaSlot.ViewingContent;
		this.selectedContentId = this.filteredComponents[0].contentId;
		this.considerUploadedData = value;
		this.convertedData = [];
		this.convertedDataForTable = [];
		this.dataFromCSV = [];
		ReportsModule.setMediaChangeImpactReportData([]);
	}

	setComponents(value) {
		this.components = value;
	}

	get contents() {
		return ReportsModule.contents;
	}

	get drawerTitle() {
		return this.uploadedFileName.replace(this.acceptedExtension, "");
	}

	validateDataFromUpload() {
		this.mediaChangeImpactReportService.getReportData(this.convertedData, this.considerUploadedData).then((res) => {
			if (res.validationStatus) {
				ReportsModule.setMediaChangeImpactReportData(res.result);
			} else {
				res.result.forEach((ele) => {
					this.errors.push(ele.index);
				});
			}
		});
	}

	getReportDataFromUpload() {
		ReportsModule.setMediaChangeImpactReportGridLoading(true);
		ReportsModule.setMediaChangeImpactReportData(ReportsModule.mediaChangeImpactReportData);
		ReportsModule.setMediaChangeImpactReportGridLoading(false);
	}

	getReportDataFromFilters() {
		ReportsModule.setMediaChangeImpactReportGridLoading(true);
		const requestBody = [
			{
				Index: 0,
				ContentId: this.selectedContentId,
				Unit: this.unitObject.startUnitId,
				MediaTypeId: this.selectedMediaSlotId,
				Component: null
			}
		];
		this.mediaChangeImpactReportService
			.getReportData(requestBody, this.considerUploadedData)
			.then((res) => {
				ReportsModule.setMediaChangeImpactReportData(res.result);
				ReportsModule.setMediaChangeImpactReportGridLoading(false);
			})
			.catch((err) => {
				console.log(err);
				ReportsModule.setMediaChangeImpactReportGridLoading(false);
			});
	}

	checkFileExtension(fileExt) {
		return fileExt == "text/csv";
	}

	beforeUpload(file: File) {
		this.errors = [];
		this.uploadedFileName = file.name;
		if (this.checkFileExtension(file.type)) {
			const reader = new FileReader();
			reader.onload = (event) => {
				if (event && event.target && event.target.result) {
					const csvData = event.target.result;
					const workbook = read(csvData, { type: "string", raw: true });
					const sheetName = workbook.SheetNames[0];
					const sheet = workbook.Sheets[sheetName];
					const data = utils.sheet_to_json(sheet, { header: 1 });
					this.dataFromCSV = cloneDeep(data.slice(1));
					this.convertData();
					this.convertDataForTable();
				}
			};
			reader.readAsText(file);

			MessageHelper.Message(NotificationType.Success, "File Uploaded Successfully");
		} else {
			MessageHelper.Message(
				NotificationType.Failed,
				`File type not acceptable, please Upload ${this.acceptedExtension} file`
			);
		}
	}

	openDrawer() {
		this.isDrawerVisible = true;
	}

	onDrawerClose() {
		this.isDrawerVisible = false;
	}

	getMediaTypeIdFromMediaType(mediaType: string) {
		const mediaTypeMap = {
			[MediaSlotTypeMapForView[MediaSlot.ViewingContent]
				.toLowerCase()
				.replace(/\s/g, "")]: MediaSlot.ViewingContent,
			[MediaSlotTypeMapForView[MediaSlot.AlternateContent]
				.toLowerCase()
				.replace(/\s/g, "")]: MediaSlot.AlternateContent,
			[MediaSlotTypeMapForView[MediaSlot.Audio].toLowerCase().replace(/\s/g, "")]: MediaSlot.Audio,
			[MediaSlotTypeMapForView[MediaSlot.AudioBackground]
				.toLowerCase()
				.replace(/\s/g, "")]: MediaSlot.AudioBackground,
			[MediaSlotTypeMapForView[MediaSlot.StreamingVideo]
				.toLowerCase()
				.replace(/\s/g, "")]: MediaSlot.StreamingVideo,
			[MediaSlotTypeMapForView[MediaSlot.OfflineVideo].toLowerCase().replace(/\s/g, "")]: MediaSlot.OfflineVideo,
			[MediaSlotTypeMapForView[MediaSlot.TeacherManual]
				.toLowerCase()
				.replace(/\s/g, "")]: MediaSlot.TeacherManual,
			[MediaSlotTypeMapForView[MediaSlot.Thumbnail].toLowerCase().replace(/\s/g, "")]: MediaSlot.Thumbnail,
			[MediaSlotTypeMapForView[MediaSlot.ExposureAudio]
				.toLowerCase()
				.replace(/\s/g, "")]: MediaSlot.ExposureAudio,
			[MediaSlotTypeMapForView[MediaSlot.ExposureVideo]
				.toLowerCase()
				.replace(/\s/g, "")]: MediaSlot.ExposureVideo,
			[MediaSlotTypeMapForView[MediaSlot.Image].toLowerCase().replace(/\s/g, "")]: MediaSlot.Image,
			[MediaSlotTypeMapForView[MediaSlot.IndividualAudio]
				.toLowerCase()
				.replace(/\s/g, "")]: MediaSlot.IndividualAudio
		};

		const normalizedMediaType = mediaType.toLowerCase().replace(/\s/g, "");

		return mediaTypeMap[normalizedMediaType];
	}

	convertData() {
		const properties = ["Unit", "Component", "MediaTypeId"];
		this.convertedData = this.dataFromCSV.map((row, index) => {
			const obj = {};
			properties.forEach((column, index) => {
				if (column == "MediaTypeId") {
					obj[column] = this.getMediaTypeIdFromMediaType(row[index]);
				} else if (column == "Unit") {
					obj[column] = Number(row[index]);
				} else {
					obj[column] = row[index];
				}
			});
			return obj;
		});
		this.convertedData.map((ele, index) => {
			ele.Index = index + 1;
			ele.ContentId = null;
		});
		this.validateDataFromUpload();
	}

	convertDataForTable() {
		const properties = ["Unit", "Component", "MediaTypeId"];
		this.convertedDataForTable = this.dataFromCSV.map((row, index) => {
			const objForTable = {};
			properties.forEach((column, index) => {
				if (column == "Unit") {
					objForTable[column] = Number(row[index]);
				} else {
					objForTable[column] = row[index];
				}
			});
			return objForTable;
		});
		this.convertedDataForTable.map((ele, index) => {
			ele.Index = index + 1;
		});
	}

	showOpenDrawer() {
		return !this.dataFromCSV.length;
	}

	dummyRequest() {
		setTimeout(() => {}, 0);
	}

	handleComponentSearch(value) {
		this.filteredComponents = this.components.filter((component: any) => {
			return component.contentName.toLowerCase().includes(value.toLowerCase());
		});
	}

	@Watch("components")
	updateFilteredComponents() {
		this.filteredComponents = cloneDeep(this.components);
	}

	get isSearchDisabled() {
		return this.considerUploadedData && this.dataFromCSV.length && this.errors.length !== 0;
	}
}
